<template>
  <div class="border-top">
    <div class="communication" :class="{'fixed': isFixed }" pa-0>
      <template v-if="isMobile">
        <transition name="slide-left">
          <filter-message-container
            v-if="!selectedThread && !showNewThreadForm"
            :saved-sort="savedSort"
            :threads="threads"
            @update-saved-sort="saveSort($event)"
            @on-new-thread="removeShowingThread()"
          />
        </transition>
        <transition name="slide-right">
          <div v-if="selectedThread || showNewThreadForm">
            <template v-if="selectedThread">
              <thread-header-manager
                v-if="threadHeaderNeeded"
                :thread="selectedThread.thread"
                @open-home-details="openHomeDetails($event)"
              />
              <div class="user-dialog_wrapper" :class="{ 'no-reply-message': !showReplyMessage }">
                <messages-list
                  :messages="selectedThread.messages"
                  :sender="clientData"
                  :receiver="associatedAgent"
                  :client-routes="clientRouteData"
                  @on-provide-feedback="openMobileFeedbackModal($event)"
                />
              </div>
              <reply-message v-if="showReplyMessage" @on-submit="onSubmitNewMessage($event)" />
            </template>
            <template v-if="showNewThreadForm">
              <new-thread :receiver="associatedAgent" @on-message="startNewThread($event)" />
            </template>
          </div>
        </transition>
        <property-view-modal
          :home-info="homeInfo"
          :show-modal="showHomeInfoModal"
          @close-modal="showHomeInfoModal=false"
        />
      </template>
      <div v-else id="containerClientCommunication" wrap>
        <v-flex xs12 md12 class="messages-section">
          <v-flex md3 sm5>
            <filter-message-container
              :threads="threads"
              :saved-sort="savedSort"
              @update-saved-sort="saveSort($event)"
              @on-new-thread="removeShowingThread()"
            />
          </v-flex>
          <v-flex md9 sm7>
            <!-- <client-info-header :name="name" :price="price" :goal-date="date" /> -->
            <div v-if="selectedThread" class="message-section__thread">
              <thread-header-manager v-if="threadHeaderNeeded" :thread="selectedThread.thread" />
              <div class="user-dialog_wrapper" :class="{ 'no-reply-message': !showReplyMessage }">
                <messages-list
                  :messages="selectedThread.messages"
                  :sender="clientData"
                  :receiver="associatedAgent"
                  :client-routes="clientRouteData"
                  @on-provide-feedback="openFeedbackModal($event)"
                />
              </div>
              <reply-message v-if="showReplyMessage" @on-submit="onSubmitNewMessage($event)" />
            </div>
            <div v-else-if="associatedAgent" class="message-section__thread">
              <new-thread :receiver="associatedAgent" @on-message="startNewThread($event)" />
            </div>
          </v-flex>
        </v-flex>
      </div>
    </div>
    <template v-if="selectedFeedbackData">
      <template v-if="!isMobile">
        <v-dialog
          v-model="displayFeedbackModal"
          :width="modal.xlarge_width"
          :max-width="modal.xlarge_maxWidth"
          persistent
        >
          <feedback-component
            :property-data="selectedProperty"
            :feedback-data="selectedFeedbackData"
            @on-feedback-updated="updateFeedback($event)"
            @close="closeFeedbackDetailsModal"
          />
        </v-dialog>
      </template>
      <template v-if="showMobileFeedbackModal">
        <feedback-component
          class="mobile-feedback-component"
          :property-data="selectedProperty"
          :feedback-data="selectedFeedbackData"
          @on-feedback-updated="updateFeedback($event)"
          @close="closeFeedbackDetailsModal"
        />
      </template>
    </template>
  </div>
</template>

<script>
import uuidv4 from 'uuid/v4'
import { get } from 'lodash'
import { sendMessage, createNewThread } from '../../services/messagesService'
import MessagesList from '../../components/communication/MessagesList'
import FilterMessageContainer from '../../components/communication/FilterMessageContainer'
import ThreadHeaderManager from '../../components/communication/thread-headers/ThreadHeaderManager'
import NewThread from '../../components/communication/NewThread'
import ReplyMessage from '../../components/communication/ReplyMessage'
import { getPropertyById } from '../../services/propertiesService'
import PropertyViewModal from '../../components/modals/PropertyViewModal'
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'

export default {
  components: {
    ReplyMessage,
    MessagesList,
    FilterMessageContainer,
    ThreadHeaderManager,
    NewThread,
    PropertyViewModal
  },
  data() {
    return {
      displayFeedbackModal: false,
      selectedFeedbackData: null,
      selectedProperty: null,
      savedSort: null,
      threadTypes: [
        'home_message',
        'provide_feedback_message',
        'offer_message',
        'common_message',
        'route_schedule_message'
      ],
      showHomeInfoModal: false,
      homeInfo: null
    }
  },
  computed: {
    ...mapState('common', ['isMobile', 'showMobileFeedbackModal', 'showSingleRoutePropertyView']),
    ...mapState('client', [
      'clientData',
      'associatedAgent',
      'clientMessages',
      'clientRouteData'
    ]),
    ...mapState('communication', [
      'selectedThread',
      'showNewThreadForm',
      'threads'
    ]),
    ...mapState('style', ['modal']),
    ...mapGetters('client', ['clientUnreadMessages']),

    isFixed() {
      if (!this.isMobile) return false
      if (this.showSingleRoutePropertyView) return false
      return true
    },

    showReplyMessage() {
      return get(this.selectedThread, 'thread.canReply', true)
    },
    threadHeaderNeeded() {
      if (!this.selectedThread) return false
      return this.threadTypes.includes(this.selectedThread.thread.type)
    },
    getClientRoutes() {
      if (this.selectedDialogUser) {
        return this.clientRouteData.filter(
          route => route.clientId === this.selectedDialogUser._id
        )
      }
      return []
    }
  },
  watch: {
    selectedThread() {
      this.setShowingMobileBackButton(
        !!this.selectedThread || !!this.showNewThreadForm
      )
    },
    showNewThreadForm() {
      this.setShowingMobileBackButton(
        !!this.selectedThread || !!this.showNewThreadForm
      )
    },
    clientMessages() {}
  },
  mounted() {
    if (!this.isMobile) {
      this.selectThread(this.threads[this.threads.length - 1])
    }
    this.markAsRead()
  },
  destroyed() {
    this.selectThread(null)
    this.setShowNewThreadForm(false)
    this.closeFeedbackDetailsModal()
  },
  methods: {
    ...mapActions('client', ['markMessagesAsRead', 'getDialogsUsers']),
    ...mapMutations('client', {
      setDialogUser: 'SET_DIALOG_USER',
      setClientMessages: 'SET_CLIENT_MESSAGES',
      setClientProvideFeedback: 'SET_CLIENT_PROVIDE_FEEDBACK'
    }),
    ...mapMutations('communication', {
      selectThread: 'SET_THREAD',
      setThreads: 'SET_THREADS',
      setShowNewThreadForm: 'SET_SHOW_NEW_THREAD_FORM'
    }),
    ...mapMutations('mobile', {
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),
    ...mapMutations('common', {
      setSelectedHome: 'SET_SELECTED_HOME',
      setPersonName: 'SET_PERSON_NAME',
      setShowMobileFeedbackModal: 'SET_SHOW_MOBILE_FEEDBACK_MODAL'
    }),

    saveSort(sort) {
      this.savedSort = sort
    },

    async markAsRead() {
      await this.markMessagesAsRead({
        user: this.associatedAgent,
        messages: this.clientUnreadMessages
      })
    },

    async onSubmitNewMessage(message) {
      const newMessage = (await sendMessage({
        to: [this.associatedAgent._id],
        message,
        subject: 'Chat message',
        metadata: {
          data: {
            thread: this.selectedThread.thread
          }
        }
      })).data
      newMessage.from = this.clientData._id
      const th = { ...this.selectedThread }
      th.messages.push(newMessage)
      this.selectThread(th)
    },

    updateFeedback(feedback) {
      this.selectedFeedbackData = feedback
    },

    async openFeedbackModal(item) {
      this.selectedProperty = (await getPropertyById(item.metadata.data.thread.property_id)).data
      this.selectedFeedbackData = this.clientData.homes.find(
        home => home.propertyId === item.metadata.data.thread.property_id
      )
      this.displayFeedbackModal = true
    },

    async openMobileFeedbackModal(item) {
      this.selectedProperty = (await getPropertyById(item.metadata.data.thread.property_id)).data
      this.selectedFeedbackData = this.clientData.homes.find(
        home => home.propertyId === item.metadata.data.thread.property_id
      )
      this.setPersonName(this.clientData.name)
      this.setShowMobileFeedbackModal(true)
      this.setSelectedHome(this.selectedFeedbackData)
    },

    closeFeedbackDetailsModal() {
      this.setShowMobileFeedbackModal(false)
      this.setSelectedHome(null)
      this.displayFeedbackModal = false
      this.selectedFeedbackData = null
    },

    removeShowingThread() {
      this.setShowNewThreadForm(true)
      this.selectThread(null)
    },
    async startNewThread(data) {
      const result = (await createNewThread({
        to: [this.associatedAgent._id],
        message: data.message,
        subject: data.subject,
        threadId: uuidv4()
      })).data

      result.from = this.clientData._id
      this.setThreads([...this.threads, {
        messages: [result],
        thread: result.metadata.data.thread
      }])
      this.setShowNewThreadForm(false)
      this.selectThread(this.threads[this.threads.length - 1])
    },
    openHomeDetails(home) {
      this.showHomeInfoModal = true
      this.homeInfo = home
    }
  }
}
</script>

<style scoped lang="sass">
.messages-section
  background: $hwayzWhite
  border-left: 1px solid #BEBEBE
  height: calc(100vh - 49px)
  display: flex
  flex-direction: row

.list-section
  padding: 25px 20px 0

.messages-section__title
  margin-bottom: 20px
  text-align: left
  padding-left: 20px

.border-top
  border-top: 1px solid #BEBEBE

.communication
  background: $hwayzBackgroundColor
  display: block
  max-width: 100%
  margin: 0
  overflow: scroll
  align-items: stretch
  height: calc(100vh - 49px)

.communication-panel-container .panel-container__title
  position: static

.communication-panel-container .panel-container__content
  padding-top: 0px
.thread-header + .user-dialog_wrapper
  height: calc(100vh - 209px)
  @media only screen and (max-width: $bp-phone-only)
    height: calc(100% - 148px)

.user-dialog_wrapper.no-reply-message
  height: calc(100vh - 49px)

.user-dialog_wrapper
  height:  calc(100vh - 165px)
  min-height: 0px
  overflow-y: scroll
@media only screen and (max-width: $bp-phone-only)
  .messages-section__title
    text-align: center
    padding-left: 0
    font-size: 16px
    margin: 10px

  .user-dialog_wrapper
    height: 100px

  .messages-section
    height: calc(100vh - 49px)

.communication.fixed
    width: 100%
    height: 100%
    padding-top: 48px
    position: fixed
    overflow: hidden
    top: 0
    > div
      height: 100%
</style>
